import { createContext, FC, ReactElement, useContext, useEffect, useReducer, useState } from "react";
import { Workspace } from "../../types/workspace";
import { Organization } from "../../types/organization";
import { useNavigate } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { useApplicationContext } from "./ApplicationContext";
import { getWorkspacesByOrgId } from "../../api/workspace";
import { Anonymity, RnD } from "../../types/enums";
import { Project } from "../../types/project";
import { PaletteMode, responsiveFontSizes, ThemeProvider } from "@mui/material";
import { getTheme } from "../../theme";
import { getProjectsByWorkspaceId } from "../../api/project";
type OrganizationState = {
  organizations: Organization[];
  activeOrganization: Organization;
  activeWorkspace: Workspace;
};

interface OrganizationContextType {
  organizations: Organization[];
  activeOrganization: Organization,
  activeWorkspace: Workspace;
  setActiveWorkspace: (workspace: Workspace) => void;
  setActiveOrganization: (org: Organization) => void;
  setOrganizations: (organizations: Organization[]) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  updateProject: (project: Project) => void;
  removeProject: (project: Project) => void;
  setProjects: (projects: Project[]) => void;
  changeColorMode: () => void;
}
const OrganizationContext = createContext<OrganizationContextType | null>(null);

function useOrganizationContext(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganizationContext must be used within a WorkspaceProvider"
    );
  }
  return context;
}
type OrganizationAction =
  | { type: "SET_WORKSPACES"; payload: Workspace[] }
  | { type: "SET_ACTIVE_WORKSPACE"; payload: Workspace }
  | { type: "SET_ORGANIZATIONS"; payload: Organization[] }
  | { type: "SET_ACTIVE_ORGANIZATION"; payload: Organization }
  | { type: "UPDATE_PROJECT"; payload: Project }
  | { type: "REMOVE_PROJECT"; payload: Project }
  | { type: "SET_PROJECTS"; payload: Project[] };

const organizationReducer = (
  state: OrganizationState,
  action: OrganizationAction
): OrganizationState => {
  switch (action.type) {
    case "SET_WORKSPACES":
      return {
        ...state,
        activeOrganization: {
          ...state.activeOrganization,
          workspaces: action.payload,
        },
      };
    case "SET_ACTIVE_WORKSPACE":
      if (action.payload.workspaceId)
        localStorage.setItem("workspaceId", action.payload.workspaceId);

      return {
        ...state,
        activeWorkspace: action.payload,
      };

    case "SET_ORGANIZATIONS":
      return {
        ...state,
        organizations: action.payload,
      };
    case "SET_ACTIVE_ORGANIZATION":
      localStorage.setItem("organizationId", action.payload.id);
      return {
        ...state,
        activeOrganization: action.payload,
      };
    case "UPDATE_PROJECT":
      if (state.activeWorkspace.projects) {
        let index = state.activeWorkspace.projects?.findIndex(
          (p) => p.projectCode === action.payload.projectCode
        );
        let newProjectsArr = [...state.activeWorkspace.projects];
        if (index === -1) {
          newProjectsArr.push(action.payload);
        } else {
          newProjectsArr[index] = action.payload;
        }
        return {
          ...state,
          activeWorkspace: {
            ...state.activeWorkspace,
            projects: newProjectsArr,
          },
        };
      } else {
        return { ...state };
      }

    case "REMOVE_PROJECT":
      if (state.activeWorkspace.projects) {
        let i = state.activeWorkspace.projects.findIndex(
          (p) => p.projectCode === action.payload.projectCode
        );
        state.activeWorkspace.projects.splice(i, 1);
      }
      return { ...state };
    case "SET_PROJECTS":
      return {
        ...state,
        activeWorkspace: {
          ...state.activeWorkspace,
          projects: action.payload,
        },
      };
    default:
      return state;
  }
};
type OrganizationProviderProps = {
  children: ReactElement;
};

const initWorkspace: Workspace = {
  organizationId: "",
  workspaceId: "",
  projects: [],
  tenantName: "",
  anonymity: Anonymity.Unknown,
  diagnostics: false,
  rnd: RnD.None,
  availableDeviceTypes: [],
  displayName: "",
};
const initState: OrganizationState = {
  organizations: [],
  activeOrganization: {
    id: "",
    name: "",
    badgeLogo : "",
    logo: "",
    workspaces: [],
    primaryColor : "",
  },
  activeWorkspace: initWorkspace,
};

const OrganizationProvider: FC<OrganizationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(organizationReducer, initState);
  const navigate = useNavigate();
  const { showSnackbar } = useApplicationContext();
  const isAuthenticated = useIsAuthenticated();
  const [colorMode, setColorMode] = useState<PaletteMode>(
    (localStorage.getItem("colorMode") as PaletteMode) || "light"
  );
  const [theme, setTheme] = useState(
    responsiveFontSizes(getTheme(undefined, colorMode))
  );
  useEffect(() => {
    setTheme(
      responsiveFontSizes(
        getTheme(
          state.activeOrganization?.primaryColor,
          colorMode
        )
      )
    );
    // eslint-disable-next-line
  }, [state.activeOrganization && state.activeOrganization, colorMode]);

  const setWorkspaces = (workspaces: Workspace[]) => {
    dispatch({ type: "SET_WORKSPACES", payload: workspaces });
    
    var lastWorkspaceUsed: string | null | undefined;
    if (localStorage.getItem("workspaceId")) {
      lastWorkspaceUsed = localStorage.getItem("workspaceId");
    } else {
      lastWorkspaceUsed = workspaces[0].workspaceId;
    }
    let activeWorkspace = workspaces.find((t) => t.workspaceId === lastWorkspaceUsed);
    if (activeWorkspace) {  
      setActiveWorkspace(activeWorkspace);
      return;
    }
    if(workspaces.length > 0){
      setActiveWorkspace(workspaces[0])
    }
    else{
      setActiveWorkspace(initState.activeWorkspace);
    }
  };
  const changeColorMode = () => {
    setColorMode((prevMode) => {
      const newMode: PaletteMode = prevMode === "light" ? "dark" : "light"; // Explicitly type newMode
      localStorage.setItem("colorMode", newMode);
      return newMode;
    });
  };
  const setOrganizations = (organizations: Organization[]) => {
    dispatch({ type: "SET_ORGANIZATIONS", payload: organizations });
    // navigate("/");
  };
  const setActiveOrganization = (org: Organization) => {
    
    dispatch({ type: "SET_ACTIVE_ORGANIZATION", payload: org });
    // navigate("/");
    if (org.id) fetchWorkspaces(org.id);
  };

  const fetchWorkspaces = async (organizationId: string) => {
    if (isAuthenticated) {
      try {
        let workspaces = await getWorkspacesByOrgId(organizationId);
        setWorkspaces(workspaces);
      } catch (err: unknown) {
        if (err instanceof Error) {
          showSnackbar(err.message, "error");
        } else {
          showSnackbar("An unknown error occurred", "error");
        }
      }
    }
  };
  const setActiveWorkspace = (workspace: Workspace) => {
    dispatch({ type: "SET_ACTIVE_WORKSPACE", payload: workspace });
    // navigate("/");

    if (workspace.workspaceId)
       fetchProjects(workspace.workspaceId);
  };
  const updateProject = async (project: Project) => {
    dispatch({ type: "UPDATE_PROJECT", payload: project });
    fetchProjects(project.workspaceId);
  };

  const removeProject = (project: Project) => {
    dispatch({ type: "REMOVE_PROJECT", payload: project });
  };

  const setProjects = (projects: Project[]) => {
    dispatch({ type: "SET_PROJECTS", payload: projects });
  };

  const fetchProjects = async (workspaceId: string) => {
    if (isAuthenticated) {
        try {
          let projects = await getProjectsByWorkspaceId(workspaceId);
          setProjects(projects);
        } catch (err: unknown) {
          if (err instanceof Error) {
            showSnackbar(err.message, "error");
          } else {
            showSnackbar("An unknown error occurred", "error");
          }
        } 
    }
  };
  return (
    <OrganizationContext.Provider
      value={{
        activeWorkspace: state.activeWorkspace,
        organizations: state.organizations,
        activeOrganization: state.activeOrganization,
        setActiveOrganization,
        setOrganizations,
        setWorkspaces: setWorkspaces,
        setActiveWorkspace: setActiveWorkspace,
        updateProject,
        removeProject,
        setProjects,
        changeColorMode
      }}
    >
     <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </OrganizationContext.Provider>
  );
};

export { useOrganizationContext, OrganizationProvider };
